import { useMainStore } from "@/stores/main";
import { ApiEndpoints } from "@/static/api-endpoints";
import axios from "axios";
import { defineStore } from "pinia";

export type List = Array<{
    name: string;
    value: string;
}>;

type Lists = Record<LIST_KEYS, List>;
type SingleListResponse = { name: string; options: List };
type ListResponse = Array<SingleListResponse>;

export interface ListState {
    lists: Lists;
    gettingLists: boolean;
}

const initialLists = {
    alarm_type: [],
    building_energy_rating: [],
    claim_type: [],
    contact_preference: [],
    county: [],
    employment_status: [],
    excess_options_aviva: [],
    excess_options_allianz: [],
    family_legal_protection_addons: [],
    garden_cover_addons: [],
    heating_type: [],
    home_office_amount: [],
    insurer: [],
    interested_parties: [],
    marketing_contact_preference: [],
    occupation: [],
    property_type: [],
    residence_type: [],
    residence_holiday_home_type: [],
    residence_no_of_tenants: [],
    residence_rental_status: [],
    residence_ownership_status: [],
    roof_construction_type: [],
    specified_item_type: [],
    title: [],
    unit_of_measurement: [],
    yes_no: [
        {
            name: "Yes",
            value: "true"
        },
        {
            name: "No",
            value: "false"
        }
    ]
};

const state: ListState = {
    lists: initialLists,
    gettingLists: false
};

export type LIST_KEYS = keyof typeof initialLists;

export const useListStore = defineStore("lists<persistent>", {
    state: () => state,
    actions: {
        async getLists() {
            const main = useMainStore();
            main.setLoadingState(true);
            this.gettingLists = true;
            try {
                const listResponse = await axios.get<{ list: ListResponse }>(ApiEndpoints.GetLists);

                const listData = listResponse.data.list;
                const transformedLists = Object.fromEntries(
                    listData.map(list => {
                        const listName = list.name.split(".")[1];

                        return [listName, list.options];
                    })
                );

                this.lists = { ...this.lists, ...transformedLists };
            } finally {
                this.gettingLists = false;
                main.setLoadingState(false);
            }
        },
        async getSingleList(listName: string) {
            const main = useMainStore();
            main.setLoadingState(true);

            const listResponse = await axios.get<{ list: SingleListResponse }>(ApiEndpoints.GetSingleList(listName));
            const list = listResponse.data.list;
            try {
                if (list) {
                    const name = list.name.split(".")[1];
                    this.lists[name] = list.options;
                }
            } finally {
                main.setLoadingState(false);
            }
        }
    },
    getters: {
        getListValueName(state) {
            return (listName: LIST_KEYS, value: string) => {
                const list = state.lists[listName];
                if (!list) {
                    return value;
                }
                return list.find(item => item.value === value)?.name || value;
            };
        }
    }
});
